/**
 * Effect-to-text renderer: walks the EffectSpec union (and inline AuraDefs) and produces
 * WoW-style sentences with live numbers ("Deals 4,600 Nature damage plus 8,100 Fire damage
 * over 18 sec."). Pure; every number comes from the passed caster (or a 1,000-power default).
 *
 * Phrases are verb phrases without a subject ("deals 900 Fire damage"). `RenderCtx.mood` picks the
 * verb form so the same walk can say "Has a 30% chance to grant you ..." (inf) or "..., resetting the
 * cooldown of X" (ger). Nested aura descriptions always switch back to the third person.
 */
import type {
  AbilityDef,
  Affects,
  Amount,
  AreaAnchor,
  AreaShape,
  AreaSpec,
  AuraDef,
  CcType,
  Condition,
  EffectSpec,
  ProcSpec,
  School,
  StatMod,
  Targeting,
} from '@keystone/sim';
import { capitalize, fmtDuration, fmtInt, fmtPct, fmtSec, joinAnd, prettyId, sentence, trimNum } from './format';
import type { TooltipCaster, TooltipContent } from './types';

/** Power assumed when no caster is known (content `baseStats` power). */
export const DEFAULT_POWER = 1000;

export type Mood = '3rd' | 'inf' | 'ger';

export interface RenderCtx {
  caster: TooltipCaster | null;
  content: TooltipContent;
  /** The source is an NPC: "players" instead of "enemies", "the caster" instead of "you". */
  enemy: boolean;
  targeting: Targeting;
  resourceName(id: string): string;
  /** Shift-mode formula lines collected while rendering. */
  formulas: string[];
  /** Noun for the default target inside a zone/telegraph payload ("enemies inside"). */
  inside?: string;
  /** Zone tick interval (short "while inside" auras). */
  tickInterval?: number;
  /** Name of the ability being described (skips redundant "(Flame Shock)" labels). */
  abilityName?: string;
  /** Id of the ability being described. */
  abilityId?: string;
  /** Resources the ability already generates ("generates 10 additional Resolve"). */
  gains?: readonly string[];
  /** Snapshotted power (live aura instances). */
  powerOverride?: number;
  /** Verb form (default third person). */
  mood?: Mood;
  /** The area is the same as the previous phrase's ("... and applies Wounds to them"). */
  sameArea?: boolean;
  /** Extra aura lookup (talent-defined auras that are not in the content registry). */
  auraLookup?: (id: string) => AuraDef | undefined;
  /** Extra ability lookup (talent-added abilities). */
  abilityLookup?: (id: string) => AbilityDef | undefined;
}

type DamageE = Extract<EffectSpec, { type: 'damage' }>;
type AuraE = Extract<EffectSpec, { type: 'applyAura' }>;
type CondE = Extract<EffectSpec, { type: 'conditional' }>;
type RemoveE = Extract<EffectSpec, { type: 'removeAura' }>;

const CC_VERB: Record<CcType, string> = { stun: 'stuns', incapacitate: 'incapacitates', disorient: 'disorients', silence: 'silences', root: 'roots' };
const CC_STATE: Record<CcType, string> = { stun: 'stunned', incapacitate: 'incapacitated', disorient: 'disoriented', silence: 'silenced', root: 'rooted' };
const MAGIC: readonly School[] = ['holy', 'fire', 'nature', 'frost', 'shadow', 'arcane'];

export const schoolName = (s: School): string => capitalize(s);
const you = (ctx: RenderCtx): string => (ctx.enemy ? 'the caster' : 'you');
const your = (ctx: RenderCtx): string => (ctx.enemy ? "the caster's" : 'your');
const selfNoun = (ctx: RenderCtx): string => (ctx.enemy ? 'itself' : 'you');
const third = (ctx: RenderCtx): RenderCtx => (ctx.mood && ctx.mood !== '3rd' ? { ...ctx, mood: '3rd' } : ctx);

// ---------------------------------------------------------------------------
// Verbs
// ---------------------------------------------------------------------------

const GERUND: Record<string, string> = { set: 'setting', stun: 'stunning', drop: 'dropping', reset: 'resetting', let: 'letting' };

/** "grants" -> "grant", "applies" -> "apply", "dashes" -> "dash". */
export function toInfinitive(v: string): string {
  if (/ies$/.test(v)) return `${v.slice(0, -3)}y`;
  if (/(sh|ch|ss|x)es$/.test(v)) return v.slice(0, -2);
  return v.replace(/s$/, '');
}

function toGerund(inf: string): string {
  if (GERUND[inf]) return GERUND[inf]!;
  if (/[^e]e$/.test(inf)) return `${inf.slice(0, -1)}ing`;
  return `${inf}ing`;
}

/** A verb in the context's mood, given its third-person form. */
export function vb(ctx: RenderCtx, thirdPerson: string): string {
  const m = ctx.mood ?? '3rd';
  if (m === '3rd') return thirdPerson;
  const inf = toInfinitive(thirdPerson);
  return m === 'inf' ? inf : toGerund(inf);
}

/** "a 30%" / "an 8%" / "an 18%". */
export function aPct(p: number): string {
  const t = fmtPct(p);
  return `${/^(8|11|18)(\.|%)|^8\d/.test(t) ? 'an' : 'a'} ${t}`;
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export function getAura(ctx: RenderCtx, id: string): AuraDef | undefined {
  return ctx.content.aura(id) ?? ctx.auraLookup?.(id);
}
export function abilityName(ctx: RenderCtx, id: string): string {
  return ctx.content.ability(id)?.name ?? ctx.abilityLookup?.(id)?.name ?? prettyId(id);
}
export function auraName(ctx: RenderCtx, id: string): string {
  return getAura(ctx, id)?.name ?? prettyId(id);
}
const isHidden = (ctx: RenderCtx, id: string): boolean => !!getAura(ctx, id)?.flags?.hidden;
const abilityList = (ctx: RenderCtx, ids: readonly string[], word = 'and'): string => joinAnd(ids.map((id) => abilityName(ctx, id)), word);
const isStacking = (a: AuraDef): boolean => a.stacking === 'stack' && (a.maxStacks ?? 1) > 1;

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

export interface AmountText {
  text: string;
  /** Numeric part (base + coef × power + known % max HP), after multipliers. null = purely relative. */
  value: number | null;
}

export function amountText(a: Amount, ctx: RenderCtx, kind: 'damage' | 'heal' | 'absorb', label: string): AmountText {
  const power = ctx.powerOverride ?? ctx.caster?.power ?? DEFAULT_POWER;
  let flat = 0;
  let has = false;
  const terms: string[] = [];
  const extras: string[] = [];
  if (a.base) {
    flat += a.base;
    has = true;
    terms.push(fmtInt(a.base));
  }
  if (a.coef) {
    flat += a.coef * power;
    has = true;
    terms.push(`${trimNum(a.coef, 3)} × ${fmtInt(power)} power`);
  }
  if (a.pctSourceMaxHp) {
    if (ctx.caster?.maxHp) {
      flat += a.pctSourceMaxHp * ctx.caster.maxHp;
      has = true;
      terms.push(`${fmtPct(a.pctSourceMaxHp)} × ${fmtInt(ctx.caster.maxHp)} max HP`);
    } else extras.push(`${pctText(a.pctSourceMaxHp)} of ${your(ctx)} maximum health`);
  }
  if (a.pctTargetMaxHp) extras.push(`${pctText(a.pctTargetMaxHp)} of the target's maximum health`);
  const r = a.pctRecentDamageTaken;
  if (r) {
    const min = r.minPctMaxHp ? ` (at least ${fmtPct(r.minPctMaxHp)} of ${your(ctx)} maximum health)` : '';
    extras.push(`${fmtPct(r.pct)} of the damage ${you(ctx)} took in the last ${fmtDuration(r.windowMs)}${min}`);
  }
  const vers = 1 + (ctx.caster?.versatility ?? 0);
  const mult = vers * (kind === 'damage' ? (ctx.caster?.damageMult ?? 1) : (ctx.caster?.healMult ?? 1));
  const value = has ? flat * mult : null;
  if (value !== null) {
    const m = Math.abs(mult - 1) > 1e-9 ? ` × ${trimNum(mult, 3)}` : '';
    const v = a.variance ? ` (±${fmtPct(a.variance)})` : '';
    ctx.formulas.push(`${label}: ${terms.join(' + ')}${m} = ${fmtInt(value)}${v}`);
  }
  const text = [value !== null ? fmtInt(value) : null, ...extras].filter((x): x is string => !!x).join(' + ');
  return { text: text || '0', value };
}

/** Percent with enough precision for small values (0.4%, not 0%). */
function pctText(f: number): string {
  const p = f * 100;
  return `${trimNum(p, Math.abs(p) < 1 ? 2 : 1)}%`;
}

const isPlainNumber = (a: AmountText): boolean => a.value !== null && a.text === fmtInt(a.value);

// ---------------------------------------------------------------------------
// Areas and targets
// ---------------------------------------------------------------------------

export function affectsWord(a: Affects, ctx: RenderCtx): string {
  switch (a) {
    case 'enemies':
      return ctx.enemy ? 'players' : 'enemies';
    case 'allies':
      return 'allies';
    case 'allies_not_self':
      return 'other allies';
    default:
      return 'units';
  }
}

const SINGULAR: Record<string, string> = { enemies: 'enemy', players: 'player', allies: 'ally', 'other allies': 'other ally', units: 'unit' };
const singular = (w: string): string => SINGULAR[w] ?? w;

function anchorWord(anchor: AreaAnchor, ctx: RenderCtx): string {
  if (anchor === 'caster') return you(ctx);
  if (anchor === 'target') return 'the target';
  return 'the target location';
}

const yd = (n: number): string => `${trimNum(n, 1)} yd`;
/** "a 30 yd" / "an 8 yd" / "an 11 yd". */
const aYd = (n: number): string => {
  const t = trimNum(n, 1);
  return `${/^(8|11|18)(\.|$)|^8\d/.test(t) ? 'an' : 'a'} ${t} yd`;
};

/** Where a shape sits: "within 8 yd of you", "in a 25 yd cone in front of the caster". */
export function whereText(shape: AreaShape, anchor: AreaAnchor, ctx: RenderCtx): string {
  const at = anchorWord(anchor, ctx);
  switch (shape.type) {
    case 'circle':
      return anchor === 'point' ? `in ${aYd(shape.radius)} radius at the target location` : `within ${yd(shape.radius)} of ${at}`;
    case 'ring':
      return `${trimNum(shape.inner, 1)}–${yd(shape.outer)} away from ${at}`;
    case 'cone':
      return `in ${aYd(shape.range)} cone in front of ${at}`;
    case 'line':
      return `in ${aYd(shape.length)} line in front of ${at}`;
    case 'rect':
      return `in a ${trimNum(shape.w, 1)} by ${yd(shape.h)} area at ${at}`;
  }
}

/** "up to 3 enemies within 10 yd of the target". */
export function areaWho(area: AreaSpec, ctx: RenderCtx): string {
  if (ctx.sameArea) return 'them';
  const aff = affectsWord(area.affects, ctx);
  const other = area.excludePrimary ? 'other ' : '';
  // An NPC pulse that covers the whole room.
  if (ctx.enemy && area.anchor === 'caster' && area.shape.type === 'circle' && area.shape.radius >= 40 && !area.maxTargets) return `all ${other}${aff}`;
  const n = area.maxTargets;
  const count = n === 1 ? `1 ${other}${singular(aff)}` : n ? `up to ${n} ${other}${aff}` : `all ${other}${aff}`;
  return `${count} ${whereText(area.shape, area.anchor, ctx)}`;
}

function targetNoun(e: EffectSpec, ctx: RenderCtx): string {
  if (e.to === 'self' || e.if?.type === 'targetIsSelf') return selfNoun(ctx);
  if (e.to === 'area' && e.area) return areaWho(e.area, ctx);
  if (ctx.inside) return ctx.inside;
  if (ctx.targeting === 'self' || ctx.targeting === 'none') return selfNoun(ctx);
  return 'the target';
}

const sameArea = (a: EffectSpec | undefined, b: EffectSpec | undefined): boolean => !!a && !!b && a.to === 'area' && b.to === 'area' && !!a.area && sameJson(a.area, b.area);

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

/** Drop conditions on hidden bookkeeping auras (they mean nothing to a player). null = always true. */
export function simplifyCond(c: Condition | undefined | null, ctx: RenderCtx): Condition | null {
  if (!c) return null;
  switch (c.type) {
    case 'and': {
      const all = c.all.map((x) => simplifyCond(x, ctx)).filter((x): x is Condition => !!x);
      return all.length === 0 ? null : all.length === 1 ? all[0]! : { type: 'and', all };
    }
    case 'or': {
      const any = c.any.map((x) => simplifyCond(x, ctx)).filter((x): x is Condition => !!x);
      return any.length === c.any.length ? (any.length === 1 ? any[0]! : { type: 'or', any }) : null;
    }
    case 'not': {
      const inner = simplifyCond(c.cond, ctx);
      return inner ? { type: 'not', cond: inner } : null;
    }
    case 'selfHasAura':
    case 'selfLacksAura':
    case 'targetHasAura':
    case 'targetLacksAura':
      return isHidden(ctx, c.auraId) ? null : c;
    default:
      return c;
  }
}

function stacksOf(n: number | undefined, name: string): string {
  return n && n > 1 ? `${n} stacks of ${name}` : name;
}

/** Plain-language condition ('' when it only concerns hidden bookkeeping). */
export function condText(c0: Condition, ctx: RenderCtx): string {
  const c = simplifyCond(c0, ctx);
  if (!c) return '';
  const has = ctx.enemy ? 'has' : 'have';
  switch (c.type) {
    case 'targetHpBelow':
      return `the target is below ${fmtPct(c.pct)} health`;
    case 'targetHpAbove':
      return `the target is above ${fmtPct(c.pct)} health`;
    case 'selfHpBelow':
      return `${you(ctx)} ${ctx.enemy ? 'is' : 'are'} below ${fmtPct(c.pct)} health`;
    case 'selfHasAura':
      return `${you(ctx)} ${has} ${c.minStacks && c.minStacks > 1 ? `at least ${stacksOf(c.minStacks, auraName(ctx, c.auraId))}` : auraName(ctx, c.auraId)}`;
    case 'selfLacksAura':
      return `${you(ctx)} ${ctx.enemy ? 'does' : 'do'} not have ${auraName(ctx, c.auraId)}`;
    case 'targetHasAura': {
      const name = `${c.fromSelf ? `${your(ctx)} ` : ''}${auraName(ctx, c.auraId)}`;
      return c.minStacks && c.minStacks > 1 ? `the target has at least ${c.minStacks} stacks of ${name}` : `${name} is on the target`;
    }
    case 'targetLacksAura':
      return `${c.fromSelf ? `${your(ctx)} ` : ''}${auraName(ctx, c.auraId)} is not on the target`;
    case 'resourceAtLeast':
      return `${you(ctx)} ${has} at least ${fmtInt(c.amount)} ${ctx.resourceName(c.resource)}`;
    case 'targetIsCasting':
      return `the target is casting${c.interruptibleOnly ? ' an interruptible spell' : ''}`;
    case 'enemiesNearSelf':
      return `at least ${c.atLeast} ${ctx.enemy ? 'players' : 'enemies'} are within ${yd(c.radius)}`;
    case 'chance':
      return `a ${fmtPct(c.p)} chance succeeds`;
    case 'targetIsEnemy':
      return ctx.enemy ? 'aimed at a player' : 'used on an enemy';
    case 'targetIsSelf':
      return `the target is ${selfNoun(ctx)}`;
    case 'selfInCombat': // +basekits
      return `${you(ctx)} ${ctx.enemy ? 'is' : 'are'} in combat`;
    case 'selfAuraElapsed': // +ranger-specs (S8): the perfect-reload window
      return `${auraName(ctx, c.auraId)} has run ${c.minMs !== undefined ? `at least ${(c.minMs / 1000).toFixed(2)} sec` : ''}${c.minMs !== undefined && c.maxMs !== undefined ? ' and ' : ''}${c.maxMs !== undefined ? `at most ${(c.maxMs / 1000).toFixed(2)} sec` : ''}`;
    case 'and':
      return joinAnd(c.all.map((x) => condText(x, ctx)).filter(Boolean));
    case 'or':
      return joinAnd(c.any.map((x) => condText(x, ctx)).filter(Boolean), 'or');
    case 'not':
      return negText(c.cond, ctx);
  }
}

function negText(c: Condition, ctx: RenderCtx): string {
  switch (c.type) {
    case 'selfHasAura':
      return c.minStacks && c.minStacks > 1
        ? `${you(ctx)} ${ctx.enemy ? 'has' : 'have'} fewer than ${c.minStacks} stacks of ${auraName(ctx, c.auraId)}`
        : condText({ type: 'selfLacksAura', auraId: c.auraId }, ctx);
    case 'selfLacksAura':
      return condText({ type: 'selfHasAura', auraId: c.auraId }, ctx);
    case 'targetHasAura':
      return condText({ type: 'targetLacksAura', auraId: c.auraId, ...(c.fromSelf ? { fromSelf: true } : {}) }, ctx);
    case 'targetLacksAura':
      return condText({ type: 'targetHasAura', auraId: c.auraId, ...(c.fromSelf ? { fromSelf: true } : {}) }, ctx);
    case 'targetIsEnemy':
      return 'used on an ally';
    case 'targetHpBelow':
      return `the target is at or above ${fmtPct(c.pct)} health`;
    case 'targetHpAbove':
      return `the target is at or below ${fmtPct(c.pct)} health`;
    case 'targetIsCasting':
      return 'the target is not casting';
    case 'selfInCombat': // +basekits
      return `${you(ctx)} ${ctx.enemy ? 'is' : 'are'} out of combat`;
    default:
      return `not (${condText(c, ctx)})`;
  }
}

/** Short qualifier: "with Opening", "on an enemy", "if <cond>". */
export function condShort(c0: Condition, ctx: RenderCtx): string {
  const c = simplifyCond(c0, ctx);
  if (!c) return '';
  if (c.type === 'selfHasAura') return `with ${c.minStacks && c.minStacks > 1 ? stacksOf(c.minStacks, auraName(ctx, c.auraId)) : auraName(ctx, c.auraId)}`;
  if (c.type === 'selfLacksAura') return `without ${auraName(ctx, c.auraId)}`;
  if (c.type === 'targetIsEnemy') return 'on an enemy';
  return `if ${condText(c, ctx)}`;
}

// ---------------------------------------------------------------------------
// Stat modifiers
// ---------------------------------------------------------------------------

function modClause(m: StatMod, ctx: RenderCtx, schools?: string): string {
  const ids = m.abilityIds ?? [];
  const who = ids.length ? abilityList(ctx, ids) : null;
  const sch = schools ?? (m.school ? schoolName(m.school) : '');
  const up = m.value > 0;
  const p = fmtPct(Math.abs(m.value));
  const by = `${up ? 'increased' : 'reduced'} by ${p}`;
  const isAre = ids.length === 1 ? 'is' : 'are';
  switch (m.stat) {
    case 'damageTakenPct':
      return `${sch ? `${sch} damage` : 'damage'} taken ${by}`;
    case 'damageDonePct':
      return `${who ? `${who} damage` : sch ? `${sch} damage dealt` : 'damage dealt'} ${by}`;
    case 'healingDonePct':
      return `${who ? `${who} healing` : 'healing done'} ${by}`;
    case 'healingTakenPct':
      return `healing taken ${by}`;
    case 'hastePct':
      return `haste ${by}`;
    case 'critPct':
      return `critical strike chance ${by}`;
    case 'masteryPct':
      return `mastery ${by}`;
    case 'powerPct':
      return `power ${by}`;
    case 'moveSpeedPct':
      return m.value <= -1 ? 'unable to move' : `movement speed ${by}`;
    case 'threatPct':
      return `threat generated ×${trimNum(1 + m.value, 2)}`;
    case 'castTimePct':
      if (m.value <= -1) return `${who ?? 'spells'} ${isAre} instant`;
      return `${who ? `${who} cast time` : 'cast times'} ${by}`;
    case 'cooldownRatePct':
      return `${who ?? 'cooldowns'} recharge${ids.length === 1 ? 's' : ''} ${p} ${up ? 'faster' : 'slower'}`;
    case 'resourceCostPct':
      if (m.value <= -1) return `${who ?? 'abilities'} ${who ? isAre : 'are'} free`;
      return `${who ? `${who} resource cost` : 'resource costs'} ${by}`;
    case 'critChanceBonus':
      if (m.value >= 1) return `${who ?? 'abilities'} always critically strike${ids.length === 1 ? 's' : ''}`;
      return `${who ? `${who} critical strike chance` : 'critical strike chance'} +${p}`;
    case 'dodge':
    case 'parry':
    case 'block':
      return m.value >= 1 ? `${m.stat === 'parry' ? 'parries' : m.stat === 'dodge' ? 'dodges' : 'blocks'} all frontal melee attacks` : `${m.stat} chance ${up ? '+' : '−'}${p}`;
    case 'armor':
      return `armor ${up ? '+' : '−'}${p}`;
    case 'maxHpPct':
      return `maximum health ${by}`;
    default:
      return `${prettyId(String(m.stat))} ${trimNum(m.value, 3)}`;
  }
}

/** Verb phrase for an ability-scoped mod in a group ("are instant", "deal 50% more damage"). */
function groupedVerb(m: StatMod, plural: boolean): string | null {
  const p = fmtPct(Math.abs(m.value));
  const v = (one: string, many: string): string => (plural ? many : one);
  switch (m.stat) {
    case 'castTimePct':
      return m.value <= -1 ? v('is instant', 'are instant') : `${v('casts', 'cast')} ${p} ${m.value < 0 ? 'faster' : 'slower'}`;
    case 'damageDonePct':
      return `${v('deals', 'deal')} ${p} ${m.value > 0 ? 'more' : 'less'} damage`;
    case 'healingDonePct':
      return `${v('heals', 'heal')} for ${p} ${m.value > 0 ? 'more' : 'less'}`;
    case 'critChanceBonus':
      return m.value >= 1 ? v('always critically strikes', 'always critically strike') : `${v('has', 'have')} +${p} critical strike chance`;
    case 'cooldownRatePct':
      return `${v('recharges', 'recharge')} ${p} ${m.value > 0 ? 'faster' : 'slower'}`;
    case 'resourceCostPct':
      return m.value <= -1 ? v('is free', 'are free') : `${v('costs', 'cost')} ${p} ${m.value > 0 ? 'more' : 'less'}`;
    default:
      return null;
  }
}

/** Mods with identical stat/value/abilities but different schools merge ("magic damage taken"). */
function modClauses(mods: readonly StatMod[], ctx: RenderCtx, perStack: (m: StatMod) => boolean = () => false): string[] {
  const ps = (m: StatMod): string => (perStack(m) ? ' per stack' : '');
  // Several mods on the same ability list read as one clause ("X and Y are instant and deal 50% more damage").
  const byIds = new Map<string, StatMod[]>();
  for (const m of mods) if (m.abilityIds?.length && !m.school) byIds.set(m.abilityIds.join(','), [...(byIds.get(m.abilityIds.join(',')) ?? []), m]);
  const grouped: string[] = [];
  const used = new Set<StatMod>();
  for (const [key, g] of byIds) {
    if (g.length < 2) continue;
    const ids = key.split(',');
    const verbs = g.map((m) => groupedVerb(m, ids.length > 1));
    if (verbs.some((x) => x === null)) continue;
    grouped.push(`${abilityList(ctx, ids)} ${joinAnd(verbs as string[])}${ps(g[0]!)}`);
    for (const m of g) used.add(m);
  }
  const rest = mods.filter((m) => !used.has(m));
  const groups = new Map<string, StatMod[]>();
  for (const m of rest) {
    const k = `${m.stat}|${m.value}|${(m.abilityIds ?? []).join(',')}|${m.school ? 's' : ''}`;
    const g = groups.get(k);
    if (g) g.push(m);
    else groups.set(k, [m]);
  }
  const out: string[] = [];
  for (const g of groups.values()) {
    if (g.length > 1 && g.every((m) => m.school)) {
      const schools = g.map((m) => m.school!);
      const label = MAGIC.every((s) => schools.includes(s)) && schools.length === MAGIC.length ? 'magic' : joinAnd(schools.map(schoolName));
      out.push(modClause(g[0]!, ctx, label) + ps(g[0]!));
    } else for (const m of g) out.push(modClause(m, ctx) + ps(m));
  }
  return [...grouped, ...out];
}

/**
 * A mod as a verb phrase on its holder ("reduces your damage taken by 40%"), for signature buffs
 * named after the ability. null when the stat has no natural verb form.
 */
function modVerb(m: StatMod, ctx: RenderCtx, owner: string, obj: string): string | null {
  const up = m.value > 0;
  const p = fmtPct(Math.abs(m.value));
  const change = (what: string): string => `${vb(ctx, up ? 'increases' : 'reduces')} ${owner} ${what} by ${p}`;
  const sch = m.school ? `${schoolName(m.school)} ` : '';
  const who = m.abilityIds?.length ? abilityList(ctx, m.abilityIds) : null;
  switch (m.stat) {
    case 'damageTakenPct':
      return change(`${sch}damage taken`);
    case 'damageDonePct':
      return who ? `${vb(ctx, up ? 'increases' : 'reduces')} ${who} damage by ${p}` : change(`${sch}damage dealt`);
    case 'healingDonePct':
      return who ? `${vb(ctx, up ? 'increases' : 'reduces')} ${who} healing by ${p}` : change('healing done');
    case 'healingTakenPct':
      return change('healing taken');
    case 'hastePct':
      return change('haste');
    case 'moveSpeedPct':
      return m.value <= -1 ? `${vb(ctx, 'roots')} ${obj}` : change('movement speed');
    case 'maxHpPct':
      return change('maximum health');
    case 'dodge':
    case 'parry':
    case 'block':
      return m.value >= 1 ? `${vb(ctx, 'lets')} ${obj} ${m.stat} all frontal melee attacks` : change(`${m.stat} chance`);
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Auras
// ---------------------------------------------------------------------------

/** A single numeric damage/heal effect on the default target, if that is all a list holds. */
function simpleAmount(effects: readonly EffectSpec[], ctx: RenderCtx, label: string): { kind: 'damage' | 'heal'; school: School | null; value: number } | null {
  if (effects.length !== 1) return null;
  const e = effects[0]!;
  if ((e.type !== 'damage' && e.type !== 'heal') || (e.to && e.to !== 'target') || e.if || e.amount.perStack) return null;
  const a = amountText(e.amount, ctx, e.type, label);
  if (!isPlainNumber(a)) return null;
  return { kind: e.type, school: e.type === 'damage' ? e.school : null, value: a.value! };
}

function hastedInterval(intervalMs: number, hasteScaled: boolean | undefined, ctx: RenderCtx): number {
  return hasteScaled ? intervalMs / (1 + (ctx.caster?.haste ?? 0)) : intervalMs;
}

/** Ticks that land within a duration (the sim ticks while nextTick <= end; partial: a scaled last tick). */
export function tickCount(durationMs: number, intervalMs: number, partial = false): number {
  const n = durationMs / intervalMs;
  return partial ? n : Math.floor(n + 1e-6);
}

export const everyText = (ms: number): string => (Math.abs(ms - 1000) < 1 ? 'every sec' : `every ${fmtSec(ms)} sec`);

/** "900 Fire damage every 2 sec (8,100 over 18 sec)". */
function periodicClause(aura: AuraDef, ctx: RenderCtx): string {
  const p = aura.periodic!;
  const interval = hastedInterval(p.intervalMs, p.hasteScaled, ctx);
  const s = simpleAmount(p.effects, ctx, `${aura.name} tick`);
  const every = everyText(interval);
  if (s) {
    const what = s.kind === 'damage' ? `${fmtInt(s.value)} ${schoolName(s.school!)} damage` : `heals for ${fmtInt(s.value)}`;
    if (aura.durationMs > 0) {
      const ticks = tickCount(aura.durationMs, interval, p.partialFinalTick);
      const total = s.value * ticks;
      ctx.formulas.push(`${aura.name}: ${fmtInt(s.value)} per tick × ${trimNum(ticks, 2)} ticks = ${fmtInt(total)}`);
      return `${what} ${every} (${fmtInt(total)} over ${fmtDuration(aura.durationMs)})`;
    }
    return `${what} ${every}`;
  }
  const body = phrases(p.effects, third(ctx));
  return body ? `${every}, ${body}` : '';
}

/** Ability ids whose cast consumes a stack of this aura with no other effect ("your next X"). */
export function nextCastIds(a: AuraDef): string[] | null {
  const pr = (a.procs ?? []).filter((p) => p.consumeStack && !p.effects.length && p.abilityIds?.length && (p.on === 'castResolved' || p.on === 'cast'));
  if (!pr.length) return null;
  return [...new Set(pr.flatMap((p) => p.abilityIds!))];
}

const isConsumeOnly = (p: ProcSpec): boolean => !!p.consumeStack && !p.effects.length;

interface NextCast {
  /** "your next Riposte" / "your next 3 Lightning Bolt or Tempest casts". */
  who: string;
  /** "your next Riposte within 12 sec deals 75% more damage" (one per ability group). */
  parts: string[];
  covered: Set<StatMod>;
}

function nextCastText(a: AuraDef, ids: readonly string[], n: number, within: string, ctx: RenderCtx): NextCast {
  const pos = ctx.enemy ? 'its' : 'your';
  const who = (gids: readonly string[]): string => (n > 1 ? `${pos} next ${n} ${abilityList(ctx, gids, 'or')} casts` : `${pos} next ${abilityList(ctx, gids, 'or')}`);
  const covered = new Set<StatMod>();
  const groups = new Map<string, StatMod[]>();
  for (const m of a.mods ?? []) {
    if (!m.abilityIds?.length || m.school || !m.abilityIds.every((id) => ids.includes(id)) || groupedVerb(m, true) === null) continue;
    covered.add(m);
    const k = m.abilityIds.join(',');
    groups.set(k, [...(groups.get(k) ?? []), m]);
  }
  const parts = [...groups].map(([k, ms], i) => `${who(k.split(','))}${i === 0 ? within : ''} ${joinAnd(ms.map((m) => groupedVerb(m, n > 1)!))}`);
  return { who: who(ids), parts, covered };
}

export interface AuraClauseOpts {
  skipCc?: boolean;
  skipStacks?: boolean;
  /** Inline (inside an ability's text): no stack caps, no consume procs, no onApply (the caller phrases it). */
  inline?: boolean;
  /** Mods already described by the caller. */
  covered?: Set<StatMod>;
  /** Current stacks (live aura tooltips: "your next 2 Ripostes"). */
  stacks?: number;
}

/** Clauses describing what an aura does while active (lowercase, no period). */
export function auraClauses(aura: AuraDef, ctx0: RenderCtx, opts: AuraClauseOpts = {}): string[] {
  const ctx = { ...third(ctx0), sameArea: false, inside: undefined, abilityName: aura.name };
  const out: string[] = [];
  const stacking = isStacking(aura);
  const next = nextCastIds(aura);
  const covered = new Set(opts.covered ?? []);
  if (aura.cc && !opts.skipCc) out.push(CC_STATE[aura.cc]);
  if (next && !opts.inline) {
    const nc = nextCastText(aura, next, stacking ? Math.max(1, opts.stacks ?? 1) : 1, '', ctx);
    for (const m of nc.covered) covered.add(m);
    out.push(...(nc.parts.length ? nc.parts : [`empowers ${nc.who}`]));
  }
  const mods = (aura.mods ?? []).filter((m) => !covered.has(m));
  if (mods.length) out.push(...modClauses(mods, ctx, (m) => stacking && !m.ignoreStacks && !(next && !opts.inline)));
  if (aura.periodic) {
    const p = periodicClause(aura, ctx);
    if (p) out.push(p);
  }
  if (aura.absorb) out.push(`absorbs ${aura.absorb.schools?.length ? `${joinAnd(aura.absorb.schools.map(schoolName))} ` : ''}damage`);
  if (aura.healAbsorb) out.push('absorbs healing received');
  const f = aura.flags;
  if (f?.immuneDamage && f.immuneCc) out.push('immune to all damage and control effects');
  else if (f?.immuneDamage) out.push('immune to all damage');
  else if (f?.immuneCc) out.push('immune to control effects');
  if (f?.breakOnDamage) out.push('breaks on damage');
  if (!opts.inline && !opts.skipStacks && stacking) out.push(`stacks up to ${aura.maxStacks} times`);
  for (const s of procSentences(aura.procs ?? [], ctx, { skipConsume: true })) out.push(s);
  if (aura.onApply?.length && !opts.inline) {
    const p = phrases(aura.onApply, ctx);
    if (p) out.push(`when applied, ${p}`);
  }
  if (aura.onRemove?.length) {
    const p = phrases(aura.onRemove, ctx);
    if (p) out.push(`when it ends, ${p}`);
  }
  const x = aura.explodeOnExpire;
  if (x) {
    const dmg = x.damage ? amountText(x.damage.amount, ctx, 'damage', `${aura.name} explosion`) : null;
    const what = dmg ? `${dmg.text} ${schoolName(x.damage!.school)} damage${x.damage!.perStack ? ' per stack' : ''}` : 'an explosion';
    out.push(`when it expires, it explodes for ${what} to ${x.hitsHolder === false ? '' : 'its holder and '}allies within ${yd(x.radius)}`);
  }
  if (aura.jumpOnDispel) out.push(`jumps to the nearest ally within ${yd(aura.jumpOnDispel.radius)} when dispelled`);
  return out.filter(Boolean);
}

/** Pure DoT/HoT aura: only a periodic with one numeric effect. */
function isPlainPeriodic(a: AuraDef): boolean {
  return !!a.periodic && !a.mods?.length && !a.cc && !a.procs?.length && !a.flags?.immuneCc && !a.flags?.immuneDamage && !a.onApply?.length && !a.onRemove?.length;
}

interface AuraPhraseOpts {
  /** "or 2 with Opening": a conditional extra grant of the same aura. */
  alt?: string;
  /** " during Avenging Light": the effect's own condition, placed before any details. */
  suffix?: string;
}

function auraPhrase(e: AuraE, ctx: RenderCtx, o: AuraPhraseOpts = {}): string {
  const a = e.aura;
  const noun = targetNoun(e, ctx);
  const sub: RenderCtx = { ...third(ctx), sameArea: false };
  const n = e.stacks ?? 1;
  const isSelf = noun === 'you' || noun === 'itself';
  const grantTo = isSelf ? (ctx.enemy ? vb(ctx, 'gains') : `${vb(ctx, 'grants')} you`) : `${vb(ctx, 'grants')} ${noun}`;
  const shortZoneAura = ctx.tickInterval !== undefined && a.durationMs > 0 && a.durationMs <= ctx.tickInterval * 2;
  const dur = shortZoneAura ? ` while ${isSelf && !ctx.enemy ? 'you stand' : 'inside'}${shortZoneAura && isSelf && !ctx.enemy ? ' inside' : ''}` : a.durationMs > 0 ? ` for ${fmtDuration(a.durationMs)}` : '';
  const alt = `${o.alt ? `, ${o.alt}` : ''}${o.suffix ?? ''}`;

  if (a.flags?.hidden && !a.cc && !auraClauses(a, sub, { inline: true }).length) return '';

  // Transform window: the button becomes another ability.
  if (a.overrides?.length && isSelf) {
    const t = joinAnd(a.overrides.map((ov) => `${abilityName(ctx, ov.abilityId)} into ${abilityName(ctx, ov.with)}`));
    const rest = auraClauses(a, sub, { inline: true });
    return `${vb(ctx, 'turns')} ${t}${dur}${alt}${rest.length ? ` (${joinAnd(rest)})` : ''}`;
  }

  if (a.cc) {
    const clauses = auraClauses(a, sub, { skipCc: true, inline: true });
    return `${vb(ctx, CC_VERB[a.cc])} ${noun}${dur}${alt}${clauses.length ? ` (${a.name}: ${joinAnd(clauses)})` : ''}`;
  }

  // Proc buffs: "grants you Opening: your next Riposte within 12 sec deals 75% more damage".
  const next = nextCastIds(a);
  if (next) {
    const within = a.durationMs > 0 ? ` within ${fmtDuration(a.durationMs)}` : '';
    const nc = nextCastText(a, next, n, within, sub);
    const rest = auraClauses(a, sub, { inline: true, covered: nc.covered });
    const onApply = a.onApply?.length ? phrases(a.onApply, { ...sub, mood: 'ger' }) : '';
    const head = `${grantTo} ${a.name}${alt}${onApply ? `, ${onApply}` : ''}`;
    if (!nc.parts.length) return `${head}, ${vb({ ...ctx, mood: 'ger' }, 'empowers')} ${nc.who}${within}${rest.length ? ` (${joinAnd(rest)})` : ''}`;
    return `${head}: ${[...nc.parts, ...rest].join(', and ')}`;
  }

  // Stacks: "grants you a stack of Bastion (damage taken reduced by 1.5% per stack, up to 6)".
  if (isStacking(a)) {
    const what = n > 1 ? `${n} stacks of ${a.name}` : `a stack of ${a.name}`;
    const clauses = auraClauses(a, sub, { inline: true });
    const cap = n >= (a.maxStacks ?? 1) ? '' : `up to ${a.maxStacks}`;
    const detail = [...clauses, cap].filter(Boolean);
    const tail = detail.length ? ` (${detail.join(', ')})` : '';
    if (a.kind === 'debuff') return `${vb(ctx, 'applies')} ${what} to ${noun}${alt}${tail}`;
    return `${grantTo} ${what}${alt}${tail}`;
  }

  // Signature buff named after the ability: "reduces your damage taken by 40% for 8 sec".
  if (a.name === ctx.abilityName && !o.alt && (a.mods?.length || a.flags?.immuneCc || a.flags?.immuneDamage) && !a.periodic && !a.procs?.length && !a.onApply?.length && !a.onRemove?.length && !a.absorb) {
    const owner = isSelf ? (ctx.enemy ? 'its' : 'your') : noun === 'the target' ? "the target's" : null;
    const obj = isSelf ? selfNoun(ctx) : noun;
    if (owner) {
      const verbs = (a.mods ?? []).map((m) => modVerb(m, ctx, owner, obj));
      const f = a.flags;
      if (f?.immuneDamage || f?.immuneCc) verbs.push(`${vb(ctx, 'makes')} ${obj} immune to ${f.immuneDamage && f.immuneCc ? 'all damage and control effects' : f.immuneDamage ? 'all damage' : 'control effects'}`);
      if (verbs.length && verbs.every((v): v is string => !!v)) return `${joinAnd(verbs)}${dur}${alt}`;
    }
  }

  const clauses = auraClauses(a, sub, { inline: true });
  const onApply = a.onApply?.length ? phrases(a.onApply, { ...sub, mood: 'ger' }) : '';
  const body = clauses.length ? ` (${joinAnd(clauses)})` : '';
  const ga = onApply ? `, ${onApply}` : '';
  if (a.kind === 'debuff') return `${vb(ctx, 'afflicts')} ${noun} with ${stacksOf(n, a.name)}${dur}${alt}${body}${ga}`;
  return `${grantTo} ${stacksOf(n, a.name)}${dur}${alt}${body}${ga}`;
}

// ---------------------------------------------------------------------------
// Procs
// ---------------------------------------------------------------------------

export interface ProcOpts {
  /** Describe from this ability's point of view ("Has a 30% chance to ..." on its own tooltip). */
  perspective?: AbilityDef;
  /** Skip stack-consume-only procs (described as "your next X"). */
  skipConsume?: boolean;
}

function hasAreaDamage(a: AbilityDef): boolean {
  const walk = (list: readonly EffectSpec[] | undefined): boolean =>
    (list ?? []).some((e) => (e.type === 'damage' && e.to === 'area') || (e.type === 'conditional' && (walk(e.then) || walk(e.else))));
  return walk(a.effects) || walk(a.channelEndEffects);
}

function procSubject(pr: ProcSpec, ctx: RenderCtx, persp: AbilityDef | undefined): string | null {
  const names = pr.abilityIds?.length ? abilityList(ctx, pr.abilityIds, 'or') : null;
  const e = ctx.enemy;
  switch (pr.on) {
    case 'cast':
    case 'castResolved':
      return persp ? null : names ? `each ${names}` : e ? 'each spell it casts' : 'each spell you cast';
    case 'damageDealt':
      if (persp) return hasAreaDamage(persp) ? 'each enemy hit' : 'each hit';
      return names ? `each ${names} hit` : e ? 'each of its hits' : 'each hit you deal';
    case 'critDealt':
      return persp ? 'each critical strike' : names ? `each ${names} critical strike` : 'each critical strike you deal';
    case 'healDealt':
      return persp ? 'each heal' : names ? `each ${names} heal` : 'each heal you cast';
    case 'critHealed':
      return persp ? 'each critical heal' : names ? `each ${names} critical heal` : 'each critical heal';
    case 'interruptDealt':
      return persp ? 'each successful interrupt' : names ? `each successful interrupt with ${names}` : 'each successful interrupt';
    case 'meleeSwing':
    case 'meleeSwingLanded':
      return e ? 'each of its melee hits' : 'each melee hit you land';
    case 'avoided':
      return e ? 'each attack it dodges, parries or blocks' : 'each attack you dodge, parry or block';
    case 'damageTaken':
      return e ? 'each hit it takes' : 'each hit you take';
    case 'killCredit': // +basekits
      return 'each kill you get credit for';
    default:
      return 'when it ends';
  }
}

/** Hidden per-cast counter pattern: `if: not(selfHasAura H, minStacks k)` + an H grant. */
function counterCap(pr: ProcSpec, ctx: RenderCtx): { cap: number; effects: EffectSpec[]; cond: Condition | null } | null {
  const c = pr.if;
  if (c?.type !== 'not' || c.cond.type !== 'selfHasAura' || !isHidden(ctx, c.cond.auraId)) return null;
  const id = c.cond.auraId;
  return { cap: c.cond.minStacks ?? 1, effects: pr.effects.filter((x) => !(x.type === 'applyAura' && x.aura.id === id)), cond: null };
}

/** One proc as a lowercase clause. `alt` adds a conditional chance ("40% during Colossus Smash"). */
function procSentence(pr: ProcSpec, ctx0: RenderCtx, opts: ProcOpts, alt?: string): string {
  const ctx = third(ctx0);
  const cc = counterCap(pr, ctx);
  const effects = cc?.effects ?? pr.effects;
  const cond = cc ? null : simplifyCond(pr.if, ctx);
  const chance = pr.chance !== undefined && pr.chance < 1 ? pr.chance : null;
  const notes: string[] = [];
  if (chance !== null && pr.badLuck?.step) notes.push('rising with each miss');
  if (pr.icdMs) notes.push(`at most once every ${fmtDuration(pr.icdMs)}`);
  const subj0 = procSubject(pr, ctx, opts.perspective);
  // A cap of 1 reads as "the first enemy hit", not "each enemy hit (up to 1 per cast)".
  const first = cc?.cap === 1 && subj0 && /^each /i.test(subj0);
  const subj = first ? subj0!.replace(/^each /i, (m) => (m[0] === 'E' ? 'The first ' : 'the first ')) : cc && subj0 ? `${subj0} (up to ${cc.cap} per cast)` : subj0;
  if (cc && !subj0) notes.push(cc.cap === 1 ? 'once per cast' : `up to ${cc.cap} per cast`);
  const quiet = { ...ctx, formulas: [] as string[] };
  if (pr.on === 'auraExpire' || pr.on === 'auraRemoved') {
    const body = phrases(effects, ctx);
    return body ? `when it ${pr.on === 'auraExpire' ? 'expires' : 'ends'}, ${body}` : '';
  }
  let s: string;
  if (chance !== null) {
    const body = phrases(effects, { ...quiet, mood: 'inf' });
    if (!body) return '';
    phrases(effects, ctx); // collect formulas once
    const all = [alt, ...notes].filter(Boolean);
    const c = `${aPct(chance)} chance${all.length ? ` (${all.join(', ')})` : ''}`;
    s = `${subj ? `${subj} has` : 'has'} ${c} to ${body}`;
  } else {
    const body = phrases(effects, ctx);
    if (!body) return '';
    const all = [alt, ...notes].filter(Boolean);
    s = `${subj ? `${subj} ` : ''}${body}${all.length ? ` (${all.join(', ')})` : ''}`;
  }
  if (cond) {
    const ct = cond.type === 'selfHasAura' && !(cond.minStacks && cond.minStacks > 1) ? `while ${you(ctx)} ${ctx.enemy ? 'has' : 'have'} ${auraName(ctx, cond.auraId)}` : `if ${condText(cond, ctx)}`;
    s = `${ct}, ${s}`;
  }
  return s;
}

/**
 * Procs as clauses. A pair that only differs by chance under a has/lacks aura condition reads as
 * one ("has a 20% chance (40% during Colossus Smash) to ...").
 */
export function procSentences(procs: readonly ProcSpec[], ctx: RenderCtx, opts: ProcOpts = {}): string[] {
  const out: string[] = [];
  const used = new Set<ProcSpec>();
  const key = (p: ProcSpec): string => JSON.stringify([p.on, p.abilityIds ?? [], p.effects, p.icdMs ?? 0]);
  for (const p of procs) {
    if (used.has(p)) continue;
    if (opts.skipConsume && isConsumeOnly(p)) continue;
    // The "lacks X" (or unconditional) twin is the base; the "has X" twin is the alternative.
    const hasId = (q: ProcSpec): string | null => (q.if?.type === 'selfHasAura' && !(q.if.minStacks && q.if.minStacks > 1) ? q.if.auraId : null);
    const lacks = (q: ProcSpec, id: string): boolean =>
      !q.if || (q.if.type === 'selfLacksAura' && q.if.auraId === id) || (q.if.type === 'not' && q.if.cond.type === 'selfHasAura' && q.if.cond.auraId === id);
    const twin = procs.find((q) => q !== p && !used.has(q) && key(q) === key(p) && ((hasId(q) && lacks(p, hasId(q)!)) || (hasId(p) && lacks(q, hasId(p)!))));
    if (twin) {
      const [baseP, altP] = hasId(twin) && lacks(p, hasId(twin)!) ? [p, twin] : [twin, p];
      used.add(p);
      used.add(twin);
      const altChance = altP.chance === undefined || altP.chance >= 1 ? 'guaranteed' : fmtPct(altP.chance);
      const s = procSentence({ ...baseP, if: undefined } as ProcSpec, ctx, opts, `${altChance} during ${auraName(ctx, hasId(altP)!)}`);
      if (s) out.push(s);
      continue;
    }
    used.add(p);
    const s = procSentence(p, ctx, opts);
    if (s) out.push(s);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

const THREAT_WORD: Record<number, string> = { 2: 'double', 3: 'triple' };

function damagePhrase(e: DamageE, ctx: RenderCtx): string {
  const amt = amountText(e.amount, ctx, 'damage', `${schoolName(e.school)} damage`);
  const what = isPlainNumber(amt) ? `${amt.text} ${schoolName(e.school)} damage` : `${schoolName(e.school)} damage equal to ${amt.text}`;
  let s = `${vb(ctx, 'deals')} ${what}`;
  if (e.to === 'self') s += ` to ${selfNoun(ctx)}`;
  else if (e.to === 'area' && e.area) s += ` to ${areaWho(e.area, ctx)}`;
  else if (ctx.inside) s += ` to ${ctx.inside}`;
  if (e.split) s += ', split evenly between them';
  if (e.critBonus && e.critBonus >= 1) s += ' that always critically strikes';
  else if (e.critBonus) s += ` with +${fmtPct(e.critBonus)} critical strike chance`;
  s += perStackText(e.amount, ctx, e.to === 'area');
  if (e.threatMult && e.threatMult !== 1) s += ` (${THREAT_WORD[e.threatMult] ?? `${trimNum(e.threatMult, 2)}×`} threat)`;
  return s;
}

/**
 * A near-zero base with a huge per-stack multiplier is content's way to say "N per stack"
 * (base × (1 + mult × stacks)). Returns the per-stack amount, or null for a normal bonus.
 */
function perStackOnly(a: Amount): Amount | null {
  const p = a.perStack;
  if (!p || p.mult < 10) return null;
  const out: Amount = { ...a, perStack: undefined };
  for (const f of ['base', 'coef', 'pctSourceMaxHp', 'pctTargetMaxHp'] as const) if (out[f] !== undefined) out[f] = out[f]! * p.mult;
  return out;
}
const perStackOnlyText = (a: Amount, ctx: RenderCtx): string => ` per stack of ${auraName(ctx, a.perStack!.auraId)}${a.perStack!.on === 'target' ? ' on the target' : ''}`;

/** +rotation rework (F7): ", +10% per stack of Wounds on the target" (empty without perStack). */
export function perStackText(a: Amount, ctx: RenderCtx, area = false): string {
  const p = a.perStack;
  if (!p || !p.mult || p.mult >= 10) return '';
  const where = p.on === 'target' ? (area ? ' on each' : ' on the target') : '';
  return `, ${p.mult > 0 ? '+' : '−'}${fmtPct(Math.abs(p.mult))} per stack of ${auraName(ctx, p.auraId)}${where}`;
}

function zonePhrase(e: Extract<EffectSpec, { type: 'zone' }>, ctx: RenderCtx, sameAsPrevious: boolean): string {
  const inside = `${affectsWord(e.affects, ctx)} inside`;
  const parts: string[] = [];
  const sub3: RenderCtx = { ...third(ctx), inside, sameArea: false };
  if (e.tick) {
    const interval = hastedInterval(e.tick.intervalMs, e.tick.hasteScaled, ctx);
    const sub: RenderCtx = { ...sub3, tickInterval: interval };
    const s = simpleAmount(e.tick.effects, sub, 'Zone tick');
    if (s) {
      const ticks = tickCount(e.durationMs, interval);
      const total = s.value * ticks;
      ctx.formulas.push(`Zone: ${fmtInt(s.value)} per tick × ${ticks} ticks = ${fmtInt(total)}`);
      const what = s.kind === 'damage' ? `dealing ${fmtInt(s.value)} ${schoolName(s.school!)} damage to ${inside}` : `healing ${inside} for ${fmtInt(s.value)}`;
      parts.push(`${what} ${everyText(interval)} (${fmtInt(total)} total)`);
    } else {
      const body = phrases(e.tick.effects, sub);
      if (body) parts.push(sameAsPrevious ? body : `${everyText(interval)} it ${body}`);
    }
  }
  if (e.insideAura) {
    const c = auraClauses(e.insideAura, sub3, { inline: true });
    parts.push(`${inside} gain ${e.insideAura.name}${c.length ? ` (${joinAnd(c)})` : ''}`);
  }
  if (sameAsPrevious) return parts.join('; ');
  const head = `${vb(ctx, 'creates')} a zone ${whereText(e.shape, e.anchor, ctx)} for ${fmtDuration(e.durationMs)}`;
  return parts.length ? `${head}, ${parts.join('; ')}` : head;
}

/** Scale an effect's amount fields by k (ladder detection). */
function scaled(e: EffectSpec, k: number): EffectSpec | null {
  if (e.type !== 'damage' && e.type !== 'heal' && e.type !== 'absorb') return null;
  const a: Amount = { ...e.amount };
  const r = (x: number | undefined): number | undefined => (x === undefined ? undefined : Math.round((x * k) / 1e-9) * 1e-9);
  for (const f of ['base', 'coef', 'pctSourceMaxHp', 'pctTargetMaxHp'] as const) if (a[f] !== undefined) a[f] = r(a[f]);
  return { ...e, amount: a, if: undefined } as EffectSpec;
}

/**
 * "Heal 1% per Wounds stack" written as nested conditionals (5 → 1 stacks): one phrase
 * "heals you for 1% of your maximum health per stack of Wounds on the target (up to 5)".
 */
function ladderPhrase(e: CondE, ctx: RenderCtx): string | null {
  const steps: { k: number; eff: EffectSpec }[] = [];
  let auraId: string | null = null;
  let on: 'target' | 'self' = 'target';
  let cur: EffectSpec | undefined = e;
  const okCond = (c: Condition | undefined): c is Extract<Condition, { type: 'targetHasAura' | 'selfHasAura' }> => !!c && (c.type === 'targetHasAura' || c.type === 'selfHasAura') && (!auraId || c.auraId === auraId);
  while (cur) {
    if (cur.type === 'conditional') {
      const c = cur.cond;
      if (!okCond(c) || cur.then.length !== 1) return null;
      auraId = c.auraId;
      on = c.type === 'targetHasAura' ? 'target' : 'self';
      steps.push({ k: c.minStacks ?? 1, eff: cur.then[0]! });
      if (!cur.else) break;
      if (cur.else.length !== 1) return null;
      cur = cur.else[0];
      continue;
    }
    if (okCond(cur.if)) {
      steps.push({ k: cur.if.minStacks ?? 1, eff: cur });
      break;
    }
    return null;
  }
  if (steps.length < 2 || !auraId) return null;
  const units = steps.map((s) => scaled(s.eff, 1 / s.k));
  if (units.some((u) => !u) || !units.every((u) => sameJson(u, units[0]))) return null;
  const max = Math.max(...steps.map((s) => s.k));
  return `${effectPhraseCore(units[0]!, ctx)} per stack of ${auraName(ctx, auraId)}${on === 'target' ? ' on the target' : ''} (up to ${max} stacks)`;
}

function conditionalPhrase(e: CondE, ctx: RenderCtx, leading = false): string {
  // Internal-cooldown pattern (talents' withIcd): a selfLacksAura gate on a hidden marker aura in `then`.
  let cond: Condition | null = e.cond;
  let then = e.then;
  let icd: number | null = null;
  const gates = (c: Condition): string[] => (c.type === 'selfLacksAura' ? [c.auraId] : c.type === 'and' ? c.all.flatMap((x) => (x.type === 'selfLacksAura' ? [x.auraId] : [])) : []);
  for (const id of gates(e.cond)) {
    const marker = then.find((x) => x.type === 'applyAura' && x.to === 'self' && x.aura.id === id && x.aura.flags?.hidden);
    if (!marker || marker.type !== 'applyAura') continue;
    icd = marker.aura.durationMs;
    then = then.filter((x) => x !== marker);
    if (cond?.type === 'selfLacksAura') cond = null;
    else if (cond?.type === 'and') {
      const rest: Condition[] = cond.all.filter((x) => !(x.type === 'selfLacksAura' && x.auraId === id));
      cond = rest.length === 0 ? null : rest.length === 1 ? rest[0]! : { type: 'and', all: rest };
    }
  }
  cond = simplifyCond(cond, ctx);
  const icdText = icd ? ` (at most once every ${fmtDuration(icd)})` : '';
  const els = e.else ?? [];

  const ladder = ladderPhrase(e, ctx);
  if (ladder) return ladder;

  if (!cond) return `${phrases(then, ctx)}${icdText}`;
  if (cond.type === 'chance' && !els.length) return `${phrases(then, ctx)} (${fmtPct(cond.p)} chance)${icdText}`;

  const t0 = then[0];
  const e0 = els[0];
  // "Always crits if ..." — then/else are the same hit, then with a guaranteed crit.
  if (then.length === 1 && els.length === 1 && t0?.type === 'damage' && e0?.type === 'damage' && (t0.critBonus ?? 0) >= 1 && sameJson({ ...t0, critBonus: e0.critBonus }, e0)) {
    const s = damagePhrase(e0, ctx);
    return `${s}. Always crits if ${condText(cond, ctx)}${icdText}`;
  }
  // Same aura, more stacks under the condition: "applies a stack of Wounds to the target, or 2 with Tactician".
  if (then.length === 1 && els.length === 1 && t0?.type === 'applyAura' && e0?.type === 'applyAura' && t0.aura.id === e0.aura.id && t0.to === e0.to && (t0.stacks ?? 1) !== (e0.stacks ?? 1)) {
    return `${auraPhrase(e0, ctx, { alt: `or ${t0.stacks ?? 1} ${condShort(cond, ctx)}` })}${icdText}`;
  }
  // Damage to an enemy, heal to an ally.
  if (cond.type === 'targetIsEnemy' && then.length === 1 && els.length === 1 && t0?.type === 'damage' && e0?.type === 'heal' && !t0.to && !e0.to) {
    return `${damagePhrase(t0, ctx)} to an enemy, or ${effectPhraseCore(e0, ctx).replace(' the target ', ' an ally ')}${icdText}`;
  }

  // Shared prefix: "Deals X. If <cond>, also <rest>."
  let k = 0;
  while (k < then.length && k < els.length && sameJson(then[k], els[k])) k++;
  if (k > 0 && k === els.length && then.length > k) {
    const shared = phrases(then.slice(0, k), ctx);
    return `${shared}. If ${condText(cond, ctx)}, also ${phrases(then.slice(k), ctx)}${icdText}`;
  }
  // Consume the condition's aura: "if you have 2 stacks of Gathering Storm, consumes them and ...".
  const ct = condText(cond, ctx);
  const body = (list: readonly EffectSpec[]): string => {
    if (cond?.type === 'selfHasAura') {
      const i = list.findIndex((x) => x.type === 'removeAura' && x.to === 'self' && x.auraId === (cond as { auraId: string }).auraId);
      if (i >= 0) {
        const rest = phrases(list.filter((_, j) => j !== i), ctx);
        return joinAnd([`${vb(ctx, 'consumes')} ${cond.minStacks && cond.minStacks > 1 ? 'them' : 'it'}`, rest].filter(Boolean));
      }
    }
    return phrases(list, ctx);
  };
  if (els.length) return `if ${ct}, ${body(then)}; otherwise, ${phrases(els, ctx)}${icdText}`;
  if (leading) return `if ${ct}, ${body(then)}${icdText}`;
  const tc = trailingCond(cond, ctx);
  if (then.length === 1 && t0?.type === 'applyAura' && !t0.if) return `${auraPhrase(t0, ctx, { suffix: ` ${tc}` })}${icdText}`;
  return `${phrases(then, ctx)} ${tc}${icdText}`;
}

/** A condition placed after its effect: "during Avenging Light", "with Sudden Death", "if ...". */
function trailingCond(c0: Condition | undefined, ctx: RenderCtx): string {
  const c = simplifyCond(c0, ctx);
  if (!c || c.type === 'targetIsSelf') return '';
  if (c.type === 'selfHasAura' && !(c.minStacks && c.minStacks > 1)) {
    const a = getAura(ctx, c.auraId);
    return a && (a.ui?.proc || nextCastIds(a)) ? `with ${a.name}` : `during ${auraName(ctx, c.auraId)}`;
  }
  return `if ${condText(c, ctx)}`;
}

/** One effect as a lowercase verb phrase without a trailing period ('' = nothing worth saying). */
export function effectPhrase(e: EffectSpec, ctx: RenderCtx, prev?: EffectSpec): string {
  const c = e.if ? trailingCond(e.if, ctx) : '';
  if (c && e.type === 'applyAura') return auraPhrase(e, ctx, { suffix: ` ${c}` });
  const s = effectPhraseCore(e, ctx, prev);
  return s && c ? `${s} ${c}` : s;
}

function removeText(e: RemoveE, ctx: RenderCtx): string {
  const def = getAura(ctx, e.auraId);
  if (def?.flags?.hidden) return '';
  // The transform window this very ability consumes ("Consumes Sunfall" on Sunfall) says nothing new.
  if (e.to === 'self' && ctx.abilityId && def?.overrides?.some((o) => o.with === ctx.abilityId)) return '';
  const name = auraName(ctx, e.auraId);
  const all = def && isStacking(def) ? 'all ' : '';
  if (e.to === 'self' || (e.to === undefined && (ctx.targeting === 'self' || ctx.targeting === 'none'))) return `${vb(ctx, 'consumes')} ${all}${name}`;
  return `${vb(ctx, 'consumes')} ${all}${name} on ${targetNoun(e, ctx)}`;
}

function effectPhraseCore(e: EffectSpec, ctx: RenderCtx, prev?: EffectSpec): string {
  switch (e.type) {
    case 'damage':
      return damagePhrase(e, ctx);
    case 'heal': {
      const flip = perStackOnly(e.amount);
      const amt = amountText(flip ?? e.amount, ctx, 'heal', 'Heal');
      const per = flip ? perStackOnlyText(e.amount, ctx) : perStackText(e.amount, ctx, e.to === 'area');
      if (e.smart && e.to === 'area' && e.area) {
        const aff = affectsWord(e.area.affects, ctx);
        const who = e.smart === 1 ? `the most injured ${singular(aff)}` : `the ${e.smart} most injured ${aff}`;
        return `${vb(ctx, 'heals')} ${who} ${whereText(e.area.shape, e.area.anchor, ctx)} for ${amt.text}${per}`;
      }
      return `${vb(ctx, 'heals')} ${targetNoun(e, ctx)} for ${amt.text}${per}`;
    }
    case 'absorb': {
      const flip = perStackOnly(e.amount);
      const amt = amountText(flip ?? e.amount, ctx, 'absorb', 'Absorb');
      const per = flip ? perStackOnlyText(e.amount, ctx) : '';
      return isPlainNumber(amt)
        ? `${vb(ctx, 'shields')} ${targetNoun(e, ctx)}, absorbing the next ${amt.text} damage${per}`
        : `${vb(ctx, 'shields')} ${targetNoun(e, ctx)}, absorbing damage equal to ${amt.text}${per}`;
    }
    case 'applyAura':
      return auraPhrase(e, ctx);
    case 'removeAura':
      return removeText(e, ctx);
    case 'interrupt': {
      const noun = targetNoun(e, ctx);
      const who = noun === 'the target' ? "the target's spellcasting" : noun;
      return `${vb(ctx, 'interrupts')} ${who} and ${vb(ctx, 'locks')} out that school for ${fmtDuration(e.lockoutMs)}`;
    }
    case 'dispel': {
      const n = e.count ?? 1;
      const types = joinAnd(e.dispelTypes.map(capitalize), 'or');
      return e.harmful
        ? `${vb(ctx, 'removes')} ${n} ${types} effect${n > 1 ? 's' : ''} from ${targetNoun(e, ctx)}`
        : `${vb(ctx, 'dispels')} ${n} ${types} buff${n > 1 ? 's' : ''} from ${targetNoun(e, ctx)}`;
    }
    case 'taunt':
      return `${vb(ctx, 'taunts')} ${targetNoun(e, ctx)} to attack ${ctx.enemy ? 'it' : 'you'} for ${fmtDuration(e.durationMs)}`;
    case 'threat':
      if (e.multiply === 0) return `${vb(ctx, 'drops')} all threat`;
      if (e.multiply !== undefined) return `${vb(ctx, 'multiplies')} threat by ${trimNum(e.multiply, 2)}`;
      return e.amount ? `${vb(ctx, 'generates')} ${fmtInt(e.amount)} threat` : '';
    case 'resource': {
      const res = ctx.resourceName(e.resource);
      const on = e.to === 'self' || targetNoun(e, ctx) === 'you' ? '' : ` for ${targetNoun(e, ctx)}`;
      const more = !on && ctx.gains?.includes(e.resource) ? ' additional' : '';
      return e.amount >= 0 ? `${vb(ctx, 'generates')} ${fmtInt(e.amount)}${more} ${res}${on}` : `${vb(ctx, 'drains')} ${fmtInt(-e.amount)} ${res}${on ? ` from ${targetNoun(e, ctx)}` : ''}`;
    }
    case 'knockback':
      return e.distance >= 0 ? `${vb(ctx, 'knocks')} ${targetNoun(e, ctx)} back ${yd(e.distance)}` : `${vb(ctx, 'pulls')} ${targetNoun(e, ctx)} ${yd(-e.distance)} toward ${you(ctx)}`;
    case 'grip':
      return `${vb(ctx, 'pulls')} the target to ${you(ctx)}`;
    case 'summon': {
      const name = ctx.content.npc?.(e.npcId)?.name ?? prettyId(e.npcId);
      return `${vb(ctx, 'summons')} ${e.count > 1 ? `${e.count} ` : 'a '}${name}${e.count > 1 && !name.endsWith('s') ? 's' : ''}`;
    }
    case 'telegraph': {
      const inside = `${affectsWord(e.affects, ctx)} inside`;
      const body = phrases(e.effects, { ...third(ctx), inside, sameArea: false });
      const rep = e.repeat ? `, repeating ${e.repeat.repeats} more time${e.repeat.repeats > 1 ? 's' : ''} every ${fmtDuration(e.repeat.intervalMs)}` : '';
      return `${vb(ctx, 'marks')} an area ${whereText(e.shape, e.anchor, ctx)}${e.follow ? ' that follows its anchor' : ''}; after ${fmtDuration(e.delayMs)}, it ${body || 'erupts'}${rep}`;
    }
    case 'zone': {
      const same = prev?.type === 'zone' && prev.anchor === e.anchor && prev.durationMs === e.durationMs && sameJson(prev.shape, e.shape);
      return zonePhrase(e, ctx, same);
    }
    case 'dash':
      if (e.mode === 'toTarget') return `${vb(ctx, 'charges')} to the target${e.distance ? ` from up to ${yd(e.distance)} away` : ''}`;
      if (e.mode === 'toPoint') return `${vb(ctx, 'teleports')}${e.distance ? ` up to ${yd(e.distance)}` : ''} to the target location`;
      return `${vb(ctx, 'dashes')} ${yd(e.distance ?? 0)} forward`;
    case 'modifyCooldown': {
      const name = abilityName(ctx, e.abilityId);
      if (e.reset) return `${vb(ctx, 'resets')} the cooldown of ${name}`;
      if (e.addCharge) return `${vb(ctx, 'grants')} a charge of ${name}`;
      const d = e.deltaMs ?? 0;
      return d < 0 ? `${vb(ctx, 'reduces')} the remaining cooldown of ${name} by ${fmtDuration(-d)}` : `${vb(ctx, 'increases')} the remaining cooldown of ${name} by ${fmtDuration(d)}`;
    }
    case 'conditional':
      return conditionalPhrase(e, ctx);
    case 'setHp':
      return `${vb(ctx, 'sets')} ${targetNoun(e, ctx)} to ${fmtPct(e.pctMaxHp)} health`;
    case 'mechanic':
      // Encounter mechanics (soaks, waves, tethers...) are described by the ability's hand-written text.
      return '';
    default: {
      const t = (e as { type?: string }).type;
      return t ? `${vb(ctx, 'applies')} ${prettyId(t)}` : '';
    }
  }
}

/**
 * Merge rules shared by phrases() and renderEffects(): returns [phrase, effects consumed].
 *  - a grant followed by `conditional(cond, [same grant])`: "a stack of Bastion, or 2 with Opening"
 *  - a per-stack hit followed by removing those stacks: "..., +10% per stack of Wounds on the target, consuming them"
 */
function mergedPhrase(list: readonly EffectSpec[], i: number, ctx: RenderCtx): [string, number] | null {
  const e = list[i]!;
  const next = list[i + 1];
  if (e.type === 'applyAura' && next?.type === 'conditional' && !next.else?.length && next.then.length === 1 && !e.if) {
    const t = next.then[0]!;
    if (t.type === 'applyAura' && t.aura.id === e.aura.id && t.to === e.to && !t.if) {
      const total = (e.stacks ?? 1) + (t.stacks ?? 1);
      const short = condShort(next.cond, ctx);
      if (short) return [auraPhrase(e, ctx, { alt: `or ${total} ${short}` }), 2];
    }
  }
  if ((e.type === 'damage' || e.type === 'heal') && e.amount.perStack && next?.type === 'removeAura' && next.auraId === e.amount.perStack.auraId && (next.to ?? 'target') === (e.to ?? 'target') && (!next.area || sameJson(next.area, e.area))) {
    const p = effectPhrase(e, ctx, list[i - 1]);
    return [`${p}, consuming them`, 2];
  }
  return null;
}

/**
 * Join verb phrases. Phrases carrying a colon (proc details) go last; an Oxford comma keeps
 * participle clauses apart ("..., consuming them, and consumes Sweeping Edge").
 */
function joinClauses(parts: readonly string[]): string {
  const colon = parts.filter((p) => p.includes(': '));
  const ordered = [...parts.filter((p) => !p.includes(': ')), ...colon];
  if (colon.length > 1) return ordered.join('; ');
  if (ordered.length <= 1) return ordered[0] ?? '';
  const head = ordered.slice(0, -1);
  return head.some((p) => p.includes(',')) ? `${head.join(', ')}, and ${ordered[ordered.length - 1]}` : joinAnd(ordered);
}

/** Effects joined into one clause ("deals X and consumes Y"). */
export function phrases(list: readonly EffectSpec[], ctx: RenderCtx): string {
  const out: string[] = [];
  let lastArea: EffectSpec | undefined;
  for (let i = 0; i < list.length; i++) {
    const e = list[i]!;
    const sub = sameArea(lastArea, e) ? { ...ctx, sameArea: true } : ctx;
    const m = mergedPhrase(list, i, sub);
    const p = m ? m[0] : effectPhrase(e, sub, list[i - 1]);
    if (m) i += m[1] - 1;
    if (p) {
      out.push(p);
      if (e.to === 'area') lastArea = e;
    }
  }
  return joinClauses(out);
}

/**
 * Effects as sentences. Folds a direct hit followed by its DoT into one sentence
 * ("Deals 2,000 Fire damage plus 8,100 Fire damage over 18 sec."), keeps effects on the same
 * area together, and leads with the condition of a conditional effect ("If you have X, also ...").
 */
export function renderEffects(list: readonly EffectSpec[], ctx: RenderCtx): string[] {
  const out: string[] = [];
  for (let i = 0; i < list.length; i++) {
    const e = list[i]!;
    const next = list[i + 1];
    if (e.type === 'damage' && !e.to && !e.if && next?.type === 'applyAura' && !next.to && !next.if && next.aura.kind === 'debuff' && isPlainPeriodic(next.aura) && next.aura.durationMs > 0) {
      const p = next.aura.periodic!;
      const s = simpleAmount(p.effects, ctx, `${next.aura.name} tick`);
      if (s && s.kind === 'damage') {
        const interval = hastedInterval(p.intervalMs, p.hasteScaled, ctx);
        const ticks = tickCount(next.aura.durationMs, interval, p.partialFinalTick);
        const total = s.value * ticks;
        ctx.formulas.push(`${next.aura.name}: ${fmtInt(s.value)} every ${fmtSec(interval)} sec × ${trimNum(ticks, 2)} ticks = ${fmtInt(total)}`);
        out.push(sentence(`${damagePhrase(e, ctx)} plus ${fmtInt(total)} ${schoolName(s.school!)} damage over ${fmtDuration(next.aura.durationMs)}${next.aura.name === ctx.abilityName ? '' : ` (${next.aura.name})`}`));
        i++;
        continue;
      }
    }
    // Same area as the next effects: one sentence ("... to up to 5 enemies ... and applies Wounds to them").
    if (e.to === 'area' && !e.if) {
      let j = i + 1;
      while (j < list.length && sameArea(e, list[j]) && !list[j]!.if) j++;
      if (j > i + 1) {
        const p = phrases(list.slice(i, j), ctx);
        if (p) out.push(sentence(p));
        i = j - 1;
        continue;
      }
    }
    const m = mergedPhrase(list, i, ctx);
    if (m) {
      if (m[0]) out.push(sentence(m[0]));
      i += m[1] - 1;
      continue;
    }
    const also = out.length ? 'also ' : '';
    if (e.if && e.if.type !== 'targetIsSelf') {
      const c = condText(e.if, ctx);
      const core = effectPhraseCore(e, ctx, list[i - 1]);
      if (core) out.push(sentence(c ? `if ${c}, ${also}${core}` : core));
      continue;
    }
    if (e.type === 'conditional' && !e.else?.length) {
      const ladder = ladderPhrase(e, ctx);
      const c = simplifyCond(e.cond, ctx);
      if (!ladder && c && c.type !== 'chance' && !hasIcdMarker(e)) {
        const p = conditionalPhrase(e, ctx, true);
        if (p) out.push(sentence(also ? p.replace(/^(if [^,]+, )/, `$1${also}`) : p));
        continue;
      }
    }
    const p = effectPhrase(e, ctx, list[i - 1]);
    if (p) out.push(sentence(p));
  }
  return out;
}

function hasIcdMarker(e: CondE): boolean {
  return e.then.some((x) => x.type === 'applyAura' && x.to === 'self' && !!x.aura.flags?.hidden);
}

export function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
